import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import useNotify from '../../hooks/useNotify';
import notificationService from '../../services/notificationService';
import accessRequestService from '../../services/accessRequestService';
import { getUserDisplayName } from '../../utils/userDisplay';
import {
  ALERT_CRITICAL_EVENT,
  NOTIFICATION_NEW_EVENT,
  ACCESS_REQUEST_UPDATED_EVENT,
} from '../../services/socketService';
import { isThresholdAlertNotification } from '../../utils/thresholdAlerts';
import './NotificationBell.css';

const POLL_MS = 30000;

export default function NotificationBell({ onAccessRequestUpdated }) {
  const { user } = useAuth();
  const { showToast } = useNotify();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const wrapRef = useRef(null);
  const onUpdateRef = useRef(onAccessRequestUpdated);
  onUpdateRef.current = onAccessRequestUpdated;

  const isBridgeAdmin = user?.role === 'bridge_admin';

  const loadNotifications = useCallback(async () => {
    if (!isBridgeAdmin) return;
    try {
      const data = await notificationService.getAll();
      setNotifications(data.notifications || []);
    } catch {
      // Bell stays usable; avoid toast spam on poll failures.
    }
  }, [isBridgeAdmin]);

  useEffect(() => {
    if (!isBridgeAdmin) return undefined;
    setLoading(true);
    loadNotifications().finally(() => setLoading(false));
    const timer = setInterval(loadNotifications, POLL_MS);
    return () => clearInterval(timer);
  }, [isBridgeAdmin, loadNotifications]);

  useEffect(() => {
    if (!isBridgeAdmin) return undefined;

    const onSocket = (event) => {
      const detail = event?.detail;
      const type = detail?.type;
      if (
        type === NOTIFICATION_NEW_EVENT
        || type === ACCESS_REQUEST_UPDATED_EVENT
        || type === ALERT_CRITICAL_EVENT
      ) {
        loadNotifications();
        if (detail?.accessRequest) {
          onUpdateRef.current?.(detail.accessRequest);
        }
      }
    };

    window.addEventListener('shm:socket-event', onSocket);
    return () => window.removeEventListener('shm:socket-event', onSocket);
  }, [isBridgeAdmin, loadNotifications]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const unreadCount = notifications.filter((n) => !n.read && !n.resolved).length;

  const handleToggle = () => {
    setOpen((prev) => !prev);
    if (!open) loadNotifications();
  };

  const handleMarkRead = async (notification) => {
    if (!notification?._id || notification.read) return;
    try {
      await notificationService.markRead(notification._id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === notification._id ? { ...n, read: true } : n))
      );
    } catch {
      // Keep list usable if mark-read fails.
    }
  };

  const handleAccept = async (notification) => {
    const requestId = notification.accessRequest?._id || notification.accessRequest;
    if (!requestId) return;
    setActingId(notification._id);
    try {
      const data = await accessRequestService.accept(requestId);
      showToast('Access request accepted.');
      if (data.accessRequest) onUpdateRef.current?.(data.accessRequest);
      await loadNotifications();
    } catch (e) {
      showToast(e.message || 'Failed to accept request.', 'error');
      await loadNotifications();
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (notification) => {
    const requestId = notification.accessRequest?._id || notification.accessRequest;
    if (!requestId) return;
    setActingId(notification._id);
    try {
      await accessRequestService.reject(requestId);
      showToast('Access request rejected.');
      await loadNotifications();
    } catch (e) {
      showToast(e.message || 'Failed to reject request.', 'error');
      await loadNotifications();
    } finally {
      setActingId(null);
    }
  };

  if (!isBridgeAdmin) return null;

  return (
    <div className="bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className="bell-btn"
        onClick={handleToggle}
        aria-label="Notifications"
        title="Notifications"
      >
        <i className="ti ti-bell" />
        {unreadCount > 0 && <span className="alert-badge">{unreadCount}</span>}
      </button>

      {open && (
        <div className="bell-dropdown">
          <div className="bell-dropdown-header">Notifications</div>
          {loading && notifications.length === 0 ? (
            <div className="bell-dropdown-empty">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="bell-dropdown-empty">No notifications</div>
          ) : (
            <ul className="bell-dropdown-list">
              {notifications.map((n) => {
                const requestId = n.accessRequest?._id || n.accessRequest;
                const isThreshold = isThresholdAlertNotification(n);
                const isPending = !n.resolved && n.type === 'access_request';
                const busy = actingId === n._id;

                return (
                  <li
                    key={n._id}
                    className={`bell-item ${n.read ? 'read' : ''} ${isThreshold ? 'bell-item--critical' : ''}`}
                    onClick={() => { if (isThreshold) handleMarkRead(n); }}
                    onKeyDown={(e) => {
                      if (isThreshold && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleMarkRead(n);
                      }
                    }}
                    role={isThreshold ? 'button' : undefined}
                    tabIndex={isThreshold ? 0 : undefined}
                  >
                    <div className="bell-item-title">
                      {isThreshold && <span className="bell-item-severity-tag">Critical</span>}
                      {n.title || (isThreshold ? 'Significant threshold alert' : 'Notification')}
                    </div>
                    <div className="bell-item-message">
                      {n.message
                        || (isThreshold
                          ? `Significant threshold alert for ${n.bridgeName || n.bridge?.name || 'bridge'} — notifications sent`
                          : '')}
                    </div>
                    {isThreshold && (
                      <div className="bell-item-status critical">
                        Email / SMS / voice sent to bridge Owner, Admins, Managers, and creator
                      </div>
                    )}
                    {isPending && requestId && (
                      <div className="bell-item-actions">
                        <button
                          type="button"
                          className="btn btn-blue btn-sm"
                          disabled={busy}
                          onClick={() => handleAccept(n)}
                        >
                          {busy ? '...' : 'Accept'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={busy}
                          onClick={() => handleReject(n)}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {n.resolved && n.actionTaken === 'accepted' && (
                      <div className="bell-item-status accepted">You accepted this request</div>
                    )}
                    {n.resolved && n.actionTaken === 'rejected' && (
                      <div className="bell-item-status rejected">You rejected this request</div>
                    )}
                    {n.resolved && !n.actionTaken && !isThreshold && (
                      <div className="bell-item-status resolved">Already handled</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function getRespondedAdminName(accessRequest) {
  if (!accessRequest?.respondedBy) return null;
  return getUserDisplayName(accessRequest.respondedBy) || accessRequest.respondedBy.email;
}
